import React__default, { useCallback, useLayoutEffect } from 'react';
import { P as PopperContentContainer } from '../Popper/PopperContentContainer/PopperContentContainer-B3VjPTlg.js';
import { P as PopperContainer } from '../Popper/PopperContainer-Bz-Ue_Mo.js';
import { u as useInlinePopper } from '../Popper/useInlinePopper-DwpucsBF.js';
import { P as PopperContentContainerWrapper, O as OverflowWrapper } from './Dropdown.styles-Ds46Mep3.js';
import { i as isEventFromWithinElement } from '../LocalModules/storm-ui-utils/dist/esm/isEventFromWithinElement-BvBeBwSW-BvBeBwSW.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-vpg0lK2r-Bk1xQ7S4.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { i as isFunction } from '../LocalModules/storm-ui-utils/dist/esm/isFunction-DjxTmSal-DjxTmSal.js';
import { f as focusFirstElement } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/focusFirstElement-C1TqGpwr-LXfVb-eG.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const DropdownPopper = ({
  isOpen,
  onClose,
  offsetDistance,
  offsetSkidding,
  placement = 'bottom-start',
  enableFlip = true,
  $minWidth,
  $fullWidth,
  $noMaxHeight,
  onKeyDown,
  popperElementDidMount,
  popperRef,
  children,
  shadowRoot,
  trigger,
  focusFirstElementOnPopoverExited,
  closePopperIfTriggerClicked
}) => {
  const {
    attributes,
    styles,
    triggerElement,
    popperElement,
    setArrowElement,
    setPopperElement,
    setTriggerElement,
    update
  } = useInlinePopper({
    offsetDistance,
    offsetSkidding,
    placement,
    enableFlip,
    preventOverflow: {
      mainAxis: true
    },
    withArrow: false,
    boundaryRef: undefined,
    strategy: 'absolute'
  });

  /**
   * Click on outside popover and trigger should close the popover
   */
  const handleDocumentMousedown = useCallback(event => {
    if (typeof onClose === 'function') {
      const isNotWithinPopper = !isEventFromWithinElement(event, popperElement);
      const isNotWithinTrigger = !isEventFromWithinElement(event, triggerElement) || closePopperIfTriggerClicked;
      if (isNotWithinPopper && isNotWithinTrigger) {
        onClose(event);
      }
    }
  }, [closePopperIfTriggerClicked, onClose, popperElement, triggerElement]);

  /**
   * Handles keyboard KeyDown input when the popover surface is in focus.
   */
  const handleKeyDown = useCallback(event => {
    const {
      key
    } = event;
    if (popperElement) {
      // Trap the focus of the browser inside the popover element. Keep the user in this element
      // and allow them to tab through content until the user closes the <Popover />.
      trapTabFocus(event, popperElement, false);
    }
    if (key === KEYBOARD_KEYNAMES.SPACE) {
      event.stopPropagation();
    }
    if (isFunction(onKeyDown)) {
      onKeyDown(event);
    }
  }, [onKeyDown, popperElement]);

  /**
   * Function called once the <Transition /> component is in the entered state.
   *
   * If we are showing focus because we have had some keyboard input, we should set
   * focus to the Popover element.
   */
  const handlePopoverEntered = useCallback(() => {
    if (popperElement) {
      focusFirstElement(popperElement);
    }
    // eslint-disable-next-line max-len
    if (popperElementDidMount && isFunction(popperElementDidMount) && popperElement) {
      popperElementDidMount(popperElement);
    }
  }, [popperElementDidMount, popperElement]);
  const handlePopoverEntering = useCallback((node, isAppearing) => {
    if (!isAppearing && update !== null) {
      update();
    }
  }, [update]);

  /**
   * Function called once the <Transition /> component is in the exited state.
   */
  const handlePopoverExited = useCallback(() => {
    if (focusFirstElementOnPopoverExited && triggerElement) {
      focusFirstElement(triggerElement);
    }
  }, [focusFirstElementOnPopoverExited, triggerElement]);

  // change of $minWidth means the position of the trigger might also changed
  useLayoutEffect(() => {
    if (update) {
      update();
    }
  }, [$minWidth, update]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, trigger({
    setTriggerElement
  }), /*#__PURE__*/React__default.createElement(PopperContainer, {
    role: "listbox",
    focusableBody: true,
    attributes: attributes,
    disablePortal: false,
    handleCloseClicked: noop,
    handleKeyDown: handleKeyDown,
    handlePopoverEntered: handlePopoverEntered,
    handlePopoverEntering: handlePopoverEntering,
    handlePopoverExited: handlePopoverExited,
    isOpen: isOpen,
    onDocumentMousedown: handleDocumentMousedown,
    position: "bottom",
    setArrowElement: setArrowElement,
    setPopperElement: setPopperElement,
    styles: styles,
    type: "light",
    withCloseButton: false,
    withArrow: false,
    popperRef: popperRef,
    shadowRoot: shadowRoot,
    transitionDuration: 100,
    popoverType: "Dropdown"
  }, /*#__PURE__*/React__default.createElement(PopperContentContainerWrapper, {
    $minWidth: $minWidth,
    $fullWidth: $fullWidth,
    $noMaxHeight: $noMaxHeight
  }, /*#__PURE__*/React__default.createElement(PopperContentContainer, {
    handleCloseClicked: noop,
    padding: "none",
    type: "light",
    withCloseButton: false
  }, /*#__PURE__*/React__default.createElement(OverflowWrapper, {
    $noMaxHeight: $noMaxHeight
  }, children)))));
};
DropdownPopper.defaultProps = {
  offsetDistance: 0,
  offsetSkidding: 0,
  focusFirstElementOnPopoverExited: false,
  closePopperIfTriggerClicked: false
};

export { DropdownPopper as D };
//# sourceMappingURL=DropdownPopper-CP3TypDD.js.map
